import { Activity } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRiskMetric } from "@/hooks/useRiskMetrics";

interface SharpeBadgeProps {
  symbol: string;
  variant?: "compact" | "default";
}

function band(sharpe: number) {
  if (sharpe >= 1) return { label: "Strong", className: "bg-success/15 text-success border-success/30" };
  if (sharpe >= 0.3) return { label: "Average", className: "bg-warning/15 text-warning border-warning/30" };
  return { label: "Weak", className: "bg-destructive/15 text-destructive border-destructive/30" };
}

export function SharpeBadge({ symbol, variant = "compact" }: SharpeBadgeProps) {
  const { metric } = useRiskMetric(symbol);
  const sharpe = metric?.sharpe_90d;
  if (sharpe == null || !Number.isFinite(sharpe)) return null;

  const b = band(sharpe);
  const dd = metric?.max_drawdown_90d;
  const vol = metric?.volatility_ann_90d;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`${b.className} gap-1 cursor-help`}>
            <Activity className="w-3 h-3" />
            {variant === "compact" ? `Sharpe ${sharpe.toFixed(2)}` : `Sharpe ${sharpe.toFixed(2)} • ${b.label}`}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1 text-xs">
            <div className="font-semibold">Risk-adjusted return (90d, AUD)</div>
            <div>Sharpe: <span className="font-mono">{sharpe.toFixed(2)}</span> — {b.label}</div>
            {vol != null && <div>Annualised volatility: <span className="font-mono">{vol.toFixed(1)}%</span></div>}
            {dd != null && <div>Max drawdown (90d): <span className="font-mono">{dd.toFixed(1)}%</span></div>}
            {metric?.current_drawdown_pct != null && (
              <div>Current drawdown: <span className="font-mono">{metric.current_drawdown_pct.toFixed(1)}%</span></div>
            )}
            <div className="text-muted-foreground pt-1">Sharpe = (return − 4.35% RBA) ÷ volatility</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
